import axios, { AxiosError, AxiosRequestConfig } from 'axios';

declare const process: { env: { REACT_APP_API_URL?: string; REACT_APP_MODE?: string } };

const baseURL = process.env.REACT_APP_API_URL || '/api';

export const TOKEN_KEY = 'csa.token';
export const REFRESH_KEY = 'csa.refreshToken';

export const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const stored = localStorage.getItem(REFRESH_KEY);
  if (!stored) return null;
  try {
    const { data } = await axios.post(`${baseURL}/auth/refresh-token`, { refreshToken: stored }, { withCredentials: true });
    if (data?.token) {
      localStorage.setItem(TOKEN_KEY, data.token);
      return data.token as string;
    }
  } catch (err) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }
  return null;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retried?: boolean };
    if (error.response?.status === 401 && !original?._retried && original?.url && !original.url.includes('/auth/login') && !original.url.includes('/auth/refresh-token')) {
      original._retried = true;
      if (!refreshPromise) refreshPromise = refreshAccessToken().finally(() => { refreshPromise = null; });
      const newToken = await refreshPromise;
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` } as Record<string, string>;
        return apiClient.request(original);
      }
      // Sin refresh válido: forzar logout suave (la UI redirigirá)
      window.dispatchEvent(new CustomEvent('csa:logout'));
    }
    return Promise.reject(error);
  },
);

export default apiClient;
